import React from 'react';
import { config } from '../../../../utils/config';
import QASAddressLookUp from '../QASAddressLookUp';
import logger from '../../../../logging';
import { isPostcodeFieldTouched } from '../../../../utils/formHelpers';
import {
  QASAddressLookUpObject,
  QASSearchResult
} from '../QASAddressLookUp/QASAddressLookUp.types';
import { AddressFieldProps, AddressFieldState, CustomerAddress } from './AddressField.types';
import {
  SolarServiceabilityOptions,
  SolarProduct,
  RepairServiceabilityOptions
} from '../../../../../__generated__/globalTypes';
import { FieldProps } from 'formik';

class AddressField extends React.Component<AddressFieldProps, AddressFieldState> {
  constructor(props: AddressFieldProps) {
    super(props);

    this.state = {
      addressNotSelected: true,
      autoFindMatchingAddresses: false
    };

    this.getValues = this.getValues.bind(this);
    this.setAddress = this.setAddress.bind(this);
    this.handleOnBlur = this.handleOnBlur.bind(this);
    this.handleOnAddressSelect = this.handleOnAddressSelect.bind(this);
    this.enterAddressManually = this.enterAddressManually.bind(this);
    this.setAutoFindMatchingAddresses = this.setAutoFindMatchingAddresses.bind(this);
  }

  public componentDidMount() {
    const { form, customerAddress, field } = this.props;

    if (form.values.postcode && customerAddress) {
      this.sendQuery(form.values.postcode);
      this.setAddress(customerAddress, { field, form });
    }
  }

  public componentDidUpdate(nextProps: AddressFieldProps): void {
    const { productType, form } = this.props;
    const { autoFindMatchingAddresses, addressNotSelected } = this.state;

    if (nextProps.productType !== productType && form.values.postcode !== '') {
      this.sendQuery(form.values.postcode);
    }

    if (
      nextProps.customerAddress.label !== '' &&
      nextProps.customerAddress.qasAddress.postcode === '' &&
      !autoFindMatchingAddresses &&
      addressNotSelected
    ) {
      this.setAutoFindMatchingAddresses(true);
    }
  }

  public render(): JSX.Element {
    const {
      field,
      label,
      form,
      helpText,
      customerAddress,
      validating,
      isDisqualified,
      hasManualAddress,
      manualAddressPlaceholder
    } = this.props;
    const { autoFindMatchingAddresses } = this.state;

    const error = isDisqualified ? ' ' : form.errors[field.name];
    const currentValue = customerAddress ? customerAddress.label : undefined;

    return (
      <QASAddressLookUp
        name={field.name}
        label={label}
        value={currentValue}
        error={error}
        helpText={helpText}
        touched={isPostcodeFieldTouched(form, field.name)}
        disabled={hasManualAddress}
        data-lpignore={true}
        qasProfile="AUG"
        layout="OriginExtendedAUG"
        qasAuthToken={config.qasAuthToken}
        validating={validating}
        setAutoFindMatchingAddresses={this.setAutoFindMatchingAddresses}
        allowManualAddressEntry={true}
        manualAddressPlaceholder={manualAddressPlaceholder}
        showManualAddress={hasManualAddress}
        autoFindMatchingAddresses={autoFindMatchingAddresses}
        onEnterAddressManually={this.enterAddressManually}
        onChange={address => this.handleOnChange(address)}
        onBlur={e => this.handleOnBlur(e)}
        onAddressSelect={qasSearchResult => this.handleOnAddressSelect(qasSearchResult)}
      />
    );
  }

  private getValues = (
    value: string,
    productType: SolarProduct
  ): SolarServiceabilityOptions | RepairServiceabilityOptions => {
    return {
      address: {
        postcode: value
      },
      productType
    };
  };

  private setAutoFindMatchingAddresses = (autoFindMatchingAddresses: boolean) => {
    const { form } = this.props;
    form.setFieldTouched('address', true);
    this.setState({
      autoFindMatchingAddresses
    });
  };

  private enterAddressManually = (): void => {
    const { form, manualAddressPlaceholder, changeHandler } = this.props;

    form.setFieldValue('address', manualAddressPlaceholder, true);
    changeHandler({ hasManualAddress: true });
  };

  private clearAddress = (address: string, helpers: FieldProps): void => {
    helpers.form.setFieldValue('address', '', true);
  };

  private setAddress = (
    address: QASAddressLookUpObject | CustomerAddress,
    helpers: FieldProps
  ): void => {
    const {
      unit,
      streetNumber,
      streetName,
      streetNameOnly,
      streetType,
      city,
      suburb,
      state,
      postcode
    } = address.qasAddress;
    const { changeHandler } = this.props;

    if (unit) {
      helpers.form.setFieldValue('unit', unit, true);
    } else {
      helpers.form.setFieldValue('unit', '', true);
    }

    if (streetNameOnly && streetType) {
      helpers.form.setFieldValue('streetName', streetNameOnly, true);
      helpers.form.setFieldValue('streetType', streetType, true);
    } else {
      helpers.form.setFieldValue('streetName', streetName, true);
    }

    helpers.form.setFieldValue('streetNumber', streetNumber, true);
    helpers.form.setFieldValue('suburb', city, true);
    helpers.form.setFieldValue('city', city, true);
    helpers.form.setFieldValue('state', state, true);
    helpers.form.setFieldValue('postcode', postcode, true);
    if (address.qasAddress.label) {
      helpers.form.setFieldValue('address', address.qasAddress.label, true);
    }

    changeHandler({
      city,
      postcode,
      state,
      streetName,
      streetNameOnly,
      streetNumber,
      streetType,
      suburb: suburb ? suburb : city,
      unit: unit ? unit : ''
    });
  };

  private handleOnBlur = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { form, field } = this.props;
    const { addressNotSelected } = this.state;

    form.setFieldTouched(field.name, true, true);

    if (event.target.value === '' || addressNotSelected) {
      this.clearAddress('', { field, form });
    }
  };

  private async handleOnChange(address?: QASAddressLookUpObject) {
    const { field, form } = this.props;
    if (address && address.qasSearchResult) {
      this.setAddress(address, { field, form });
    }
  }

  private async sendQuery(postcode: string) {
    const { graphQuery, addressCheckHandler, form, productType } = this.props;

    if (!postcode) {
      return;
    }

    try {
      const { data }: any = await addressCheckHandler(
        graphQuery,
        this.getValues(postcode, productType)
      );

      if (data) {
        const checkType = data.checkServiceability
          ? data.checkServiceability
          : data.checkRepairServiceability;

        form.setFieldValue('hiddenPostcode', checkType.serviceable.toString(), true);
        form.setFieldTouched('hiddenPostcode', true, true);
      }
    } catch (error) {
      logger.error('GraphQL Error', error);
      return;
    }
  }

  private handleOnAddressSelect = (qasSearchResult: QASSearchResult): void => {
    const { form, field, changeHandler } = this.props;
    const address = qasSearchResult.label;
    const postcode = qasSearchResult.postcode;

    this.setState({
      addressNotSelected: false
    });

    changeHandler({ [field.name]: address });

    if (postcode !== form.values.postcode) {
      form.setFieldValue('hiddenPostcode', 'true', true);
    }

    form.setFieldValue(field.name, address, true);
    this.sendQuery(postcode);
  };
}

export { AddressField as default };
